import { useEffect, useState } from 'react';
import { getGPUTier } from '@pmndrs/detect-gpu';

function withFlags(base) {
  return {
    ...base,
    postFX: base.tier >= 2,
    ca: base.tier >= 3,
    skyRes: base.tier >= 2 ? '4k' : '2k',
    planetSegments: base.tier >= 2 ? 64 : 40,
  };
}

const FALLBACK_TIER = withFlags({
  label: 'T1 fallback',
  tier: 1,
  dpr: [0.75, 1],
  particles: 900,
  whitelistHeroParticles: 1400,
  asteroids: 120,
  shaderOctaves: 2,
});

const TIER_CONFIG = {
  0: { label: 'T0 static', tier: 0, dpr: [0.65, 0.85], particles: 420, whitelistHeroParticles: 2200, asteroids: 64, shaderOctaves: 1 },
  1: { label: 'T1 low', tier: 1, dpr: [0.75, 1], particles: 900, whitelistHeroParticles: 3200, asteroids: 120, shaderOctaves: 2 },
  // dpr caps raised 2026-07-12/13 so retina displays render at NATIVE
  // resolution on capable GPUs — any cap below devicePixelRatio upscales the
  // whole canvas and WebGL text can never be crisp (the ring tablets showed
  // it first). NOTE: the dpr clamp is STATIC — nothing in this app calls
  // performance.regress(), so <AdaptiveDpr> never actually downshifts; do
  // not count on it. T0/T1 (true low-end, no postFX) keep their low caps.
  2: { label: 'T2 mid', tier: 2, dpr: [0.85, 2], particles: 1600, whitelistHeroParticles: 5200, asteroids: 220, shaderOctaves: 3 },
  3: { label: 'T3 high', tier: 3, dpr: [1, 2], particles: 2600, whitelistHeroParticles: 7000, asteroids: 360, shaderOctaves: 4 },
};

export function usePerfTier() {
  const [tier, setTier] = useState(FALLBACK_TIER);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      try {
        const gpu = await getGPUTier({
          mobileTiers: [0, 1, 2, 3],
          desktopTiers: [0, 1, 2, 3],
          benchmarksURL: '/vendor/detect-gpu/benchmarks',
        });
        if (cancelled) return;
        const next = TIER_CONFIG[gpu.tier] ?? FALLBACK_TIER;
        setTier(withFlags({ ...next, gpu }));
      } catch (error) {
        if (!cancelled) setTier({ ...FALLBACK_TIER, error: String(error) });
      }
    }

    detect();
    return () => {
      cancelled = true;
    };
  }, []);

  return tier;
}
